import React, { useCallback, useState } from "react";

import { downloadExport } from "./exportDownload";
import "./ExportButton.css";

// "Export CSV" for any list page. Mount it with the page's kind and a function that returns
// the page's CURRENT list params (the same builder the page's fetch uses):
//
//     <ExportButton practiceId={id} kind="ledger" getParams={() => ledgerQueryParams(filters, sort)} />
//
// Render nothing for the read-only client role — pass `readOnly` and it hides itself. The
// server refuses that role anyway (403); hiding the button just avoids offering it.
export default function ExportButton({
  practiceId,
  kind,
  getParams,
  readOnly = false,
  label = "Export CSV",
  path = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // An absolute `path` carries no practice (the Mission Control grid), so it needs none.
  const ready = Boolean(practiceId) || Boolean(path && path.startsWith("/"));

  const run = useCallback(async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await downloadExport(practiceId, kind, getParams ? getParams() : null, path);
    } catch (err) {
      setError(err.message || "The export could not be downloaded.");
    } finally {
      setBusy(false);
    }
  }, [practiceId, kind, getParams, busy, path, ready]);

  if (readOnly) return null;

  return (
    <span className="export-button">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={run}
        disabled={busy || !ready}
        title="Download this list, with the filters you have set, as a CSV. The download is logged."
      >
        {busy ? "Exporting…" : label}
      </button>
      {error && (
        <span className="export-button-error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </span>
      )}
    </span>
  );
}
